import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getSharedRecipe, cloneSharedRecipe } from 'wasp/client/operations';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { 
  Save, 
  Plus, 
  Edit, 
  User, 
  Eye, 
  Users, 
  Clock, 
  ChefHat,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

export function SharedRecipePage() {
  const { shareSlug } = useParams<{ shareSlug: string }>();
  const navigate = useNavigate();
  const [isCloning, setIsCloning] = React.useState(false);
  const [hasCloned, setHasCloned] = React.useState(false);

  const { data, isLoading, error } = useQuery(getSharedRecipe, 
    { shareSlug: shareSlug || '' },
    { enabled: !!shareSlug }
  );

  const handleCloneRecipe = async () => {
    if (!shareSlug) return;
    
    setIsCloning(true);
    try {
      const result = await cloneSharedRecipe({
        shareSlug,
        addToMealPlan: false,
      });
      
      if (result.success) {
        setHasCloned(true);
        toast.success(result.message || 'Recipe saved to your collection!');
        
        // Optionally navigate to the cloned recipe
        setTimeout(() => {
          navigate(`/recipes`);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to clone recipe:', error);
      toast.error('Failed to save recipe. Please try again.');
    } finally {
      setIsCloning(false);
    }
  };

  const handleAddToMealPlan = async () => {
    if (!shareSlug) return;
    
    try {
      const result = await cloneSharedRecipe({
        shareSlug,
        addToMealPlan: true,
      });
      
      if (result.success) {
        toast.success('Recipe saved and added to your meal plan!');
        navigate('/plans');
      }
    } catch (error) {
      console.error('Failed to add to meal plan:', error);
      toast.error('Failed to add to meal plan. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-stone/20" />
          <div className="h-64 w-full rounded bg-stone/20" />
          <div className="h-32 w-full rounded bg-stone/20" />
        </div>
      </div>
    );
  }

  if (error || !data?.recipe) {
    return (
      <div className="container mx-auto max-w-4xl py-8">
        <Alert variant="destructive">
          <AlertTitle>Recipe Not Found</AlertTitle>
          <AlertDescription>
            This recipe doesn't exist or is not available for viewing.
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Home
        </Button>
      </div>
    );
  }

  const { recipe, isOwner, hasSaved } = data;

  return (
    <div className="min-h-screen bg-lynx pb-8">
      {/* Header */}
      <div className="border-b border-stone bg-white">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-4 py-8">
        {/* Attribution Banner */}
        <Alert className="mb-6 border-sage/30 bg-gradient-to-r from-sage/10 to-coral/10">
          <User className="h-4 w-4 text-sage" />
          <AlertTitle className="text-dusk">
            Shared by {recipe.owner?.displayName || recipe.owner?.username || 'Community Member'}
          </AlertTitle>
          <AlertDescription className="text-supporting">
            {recipe.customizationNote || 'A personalized version of this recipe'}
            {recipe.originalRecipe && (
              <span className="mt-1 block text-xs">
                Based on: {recipe.originalRecipe.title}
              </span>
            )}
          </AlertDescription>
        </Alert>

        {/* Recipe Card */}
        <Card className="overflow-hidden shadow-lg">
          {/* Recipe Image */}
          {recipe.heroImageUrl && (
            <div className="relative h-64 w-full overflow-hidden bg-stone md:h-96">
              <ImageWithFallback
                src={recipe.heroImageUrl}
                alt={recipe.title}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="font-header text-2xl text-dusk md:text-3xl">
                  {recipe.title}
                </CardTitle>
                
                {/* Tags */}
                {recipe.tags && recipe.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {recipe.tags.slice(0, 5).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recipe Meta */}
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-supporting">
              {recipe.servings && (
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {recipe.servings} servings
                </span>
              )}
              {recipe.totalMinutes && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {recipe.totalMinutes} min
                </span>
              )}
              {recipe.difficulty && (
                <span className="flex items-center gap-1">
                  <ChefHat className="h-4 w-4" />
                  {recipe.difficulty}
                </span>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Ingredients */}
            {recipe.ingredients && recipe.ingredients.length > 0 && (
              <div>
                <h3 className="mb-3 font-header text-lg font-semibold text-dusk">
                  Ingredients
                </h3>
                <ul className="space-y-2">
                  {recipe.ingredients.map((ing, index) => (
                    <li key={index} className="flex items-start gap-2 text-supporting">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-coral" />
                      <span>
                        {ing.displayQty && ing.displayUnit && (
                          <span className="font-medium text-dusk">
                            {ing.displayQty} {ing.displayUnit}
                          </span>
                        )}{' '}
                        {ing.ingredient?.name || ing.notes}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions */}
            {recipe.steps && recipe.steps.length > 0 && (
              <div>
                <h3 className="mb-3 font-header text-lg font-semibold text-dusk">
                  Instructions
                </h3>
                <ol className="space-y-3">
                  {recipe.steps.map((step, index) => (
                    <li key={step.id} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-coral/10 text-sm font-medium text-coral">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-supporting">{step.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {!isOwner && (
            <Button
              onClick={handleCloneRecipe}
              disabled={isCloning || hasSaved || hasCloned}
              className="flex-1 bg-sage font-medium text-white desktop-hover:bg-sage/90"
            >
              {isCloning ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  Saving...
                </>
              ) : hasSaved || hasCloned ? (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Saved to Collection
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save to My Recipes
                </>
              )}
            </Button>
          )}

          <Button
            onClick={handleAddToMealPlan}
            variant="outline"
            className="flex-1"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add to Meal Plan
          </Button>

          {isOwner && (
            <Button
              onClick={() => navigate(`/recipes/${recipe.id}/edit`)}
              variant="outline"
              className="flex-1"
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit Recipe
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="mt-4 flex gap-4 text-sm text-supporting">
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {recipe.viewCount || 0} views
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {recipe.saveCount || 0} saves
          </span>
        </div>
      </div>
    </div>
  );
}
